/**
 * Recommendation engine — orchestrates scoring + persistence.
 *
 * B2C twin of B2B `recommendationEngine.ts`.  Differences:
 *   • No org/multi-tenant — single user only.
 *   • No application table → results persist to `user_job_recommendations`.
 *   • No automation hooks (`runPostMatchAutomation`) — B2C surfaces
 *     recommendations for the user to act on, not for an org admin
 *     to auto-shortlist.
 */

import { supabase } from '../../../lib/supabase';
import type {
  InferenceResult,
  JobMatchInput,
  RecommendationResult,
  ScoreBreakdown,
  UserProfileMatchInput,
} from '../types';
import {
  budgetScore,
  deadlineScore,
  experienceScore,
  locationScore,
} from './scoring/dimensionScores';
import { runInference } from './scoring/inferenceEngine';
import { computeSkillOverlap } from './scoring/skillOverlap';

const TABLE = 'user_job_recommendations';

/**
 * Score a single (user, job) pair using all 6 signals + a pre-computed
 * semantic similarity (0-100 — already normalised by `normalizeCosineSimilarity`).
 */
export function scorePair(
  user: UserProfileMatchInput,
  job: JobMatchInput,
  semanticScore: number,
  synonymMap?: Map<string, string>,
): InferenceResult {
  const skillResult = computeSkillOverlap(user, job, synonymMap);

  const expVal = experienceScore(
    user.experience_years,
    job.experience_min ?? null,
    job.experience_max ?? null,
  );

  const budgetVal = budgetScore(
    user.expected_ctc,
    job.budget_min ?? null,
    job.budget_max ?? null,
  );

  const locationVal = locationScore(
    user.location,
    user.preferred_locations,
    job.location ?? null,
    job.remote_policy ?? null,
  );

  const deadlineVal = deadlineScore(
    user.notice_period_days,
    job.hiring_deadline ?? null,
  );

  return runInference(
    skillResult,
    semanticScore,
    expVal,
    budgetVal,
    locationVal,
    deadlineVal,
    user.headline ?? null,
    user.desired_roles ?? null,
    job.title,
  );
}

/**
 * Score, build a `RecommendationResult`, and upsert into
 * `user_job_recommendations` (uniqueness on user_id+job_id).
 */
export async function scoreAndPersist(
  user: UserProfileMatchInput,
  job: JobMatchInput,
  semanticScore: number,
  synonymMap?: Map<string, string>,
): Promise<RecommendationResult> {
  const inference = scorePair(user, job, semanticScore, synonymMap);

  const result: RecommendationResult = {
    user_id: user.user_id,
    job_id: job.job_id,
    score: inference.compositeScore,
    score_breakdown: inference.dimensionScores,
    matched_skills: inference.matched,
    missing_skills: inference.missing,
    bonus_skills: inference.bonus,
    why_matched: inference.recommendation,
    skill_gaps: inference.missing.slice(0, 5),
    action_items: [],
    band: inference.band,
    llm_confidence: null,
    generated_at: new Date().toISOString(),
  };

  await upsertRecommendation(result);
  return result;
}

/**
 * Upsert a recommendation row keyed on (user_id, job_id).  The
 * underlying table (TASK 3-3) declares `UNIQUE (user_id, job_id)`.
 */
export async function upsertRecommendation(
  rec: RecommendationResult,
): Promise<void> {
  const row = {
    user_id: rec.user_id,
    job_id: rec.job_id,
    score: rec.score,
    score_breakdown: rec.score_breakdown,
    why_matched: rec.why_matched,
    skill_gaps: rec.skill_gaps,
    action_items: rec.action_items,
    generated_at: rec.generated_at,
  };
  const { error } = await supabase
    .from(TABLE)
    .upsert(row, { onConflict: 'user_id,job_id' });
  if (error) {
    throw new Error(
      `[recommendationEngine] failed to upsert recommendation for ${rec.job_id}: ${error.message}`,
    );
  }
}

/**
 * Read previously persisted recommendations for a user, ordered by
 * descending score.  This is the cache the UI / agent will read first
 * before falling back to a fresh `recommendJobs` call.
 */
export async function getTopJobsForUser(
  userId: string,
  limit = 10,
): Promise<RecommendationResult[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select(
      'id, user_id, job_id, score, score_breakdown, why_matched, skill_gaps, action_items, generated_at',
    )
    .eq('user_id', userId)
    .order('score', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(
      `[recommendationEngine] failed to load recommendations for ${userId}: ${error.message}`,
    );
  }

  return (data ?? []).map((row) => mapRowToResult(row));
}

/**
 * Read a single (user, job) recommendation row — used by the
 * "Why this job?" RAG explanation in `JobDetailDrawer` (TASK 3-6).
 */
export async function getRecommendationFor(
  userId: string,
  jobId: string,
): Promise<RecommendationResult | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select(
      'id, user_id, job_id, score, score_breakdown, why_matched, skill_gaps, action_items, generated_at',
    )
    .eq('user_id', userId)
    .eq('job_id', jobId)
    .maybeSingle();

  if (error) {
    throw new Error(
      `[recommendationEngine] failed to load (${userId}, ${jobId}): ${error.message}`,
    );
  }
  return data ? mapRowToResult(data) : null;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

interface RecommendationRow {
  id?: string;
  user_id: string;
  job_id: string;
  score: number;
  score_breakdown: Partial<ScoreBreakdown> | null;
  why_matched: string | null;
  skill_gaps: string[] | null;
  action_items: string[] | null;
  generated_at: string;
}

function mapRowToResult(row: RecommendationRow): RecommendationResult {
  const breakdown: ScoreBreakdown = {
    skill_overlap:        row.score_breakdown?.skill_overlap        ?? 0,
    semantic_similarity:  row.score_breakdown?.semantic_similarity  ?? 0,
    experience_alignment: row.score_breakdown?.experience_alignment ?? 0,
    budget_fit:           row.score_breakdown?.budget_fit           ?? 0,
    location_fit:         row.score_breakdown?.location_fit         ?? 0,
    deadline_feasibility: row.score_breakdown?.deadline_feasibility ?? 0,
  };
  return {
    id: row.id,
    user_id: row.user_id,
    job_id: row.job_id,
    score: row.score,
    score_breakdown: breakdown,
    matched_skills: [],
    missing_skills: row.skill_gaps ?? [],
    bonus_skills: [],
    why_matched: row.why_matched ?? '',
    skill_gaps: row.skill_gaps ?? [],
    action_items: row.action_items ?? [],
    band: bandFromScore(row.score),
    llm_confidence: null,
    generated_at: row.generated_at,
  };
}

function bandFromScore(score: number): RecommendationResult['band'] {
  if (score >= 75) return 'strong_fit';
  if (score >= 55) return 'good_fit';
  if (score >= 35) return 'partial_fit';
  return 'not_recommended';
}
